import React, { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { apiFetch, useApp } from "../../context/AppContext";
import {
  Card,
  Badge,
  Button,
  Modal,
  Input,
  Select,
  DashboardStatCard,
  ProgressBar,
  EmptyState,
  Tooltip,
  HelpTooltip,
  LabelWithTooltip,
  Pagination,
} from "../../components/common/UI";
import { confirmAction } from "../../utils/sweetAlert";
import {
  ChartCard,
  DepartmentStatusDonut,
  AssessmentStatusDonut,
  ComplianceScoreGauge,
  DepartmentScoresBar,
  ComplianceTrendChart,
  SectionScoresBar,
  GapByDepartmentChart,
  GapTrendChart,
  YearComparisonChart,
  ScoreDeltaChart,
  GapDeltaChart,
  RecommendationsByDepartmentChart,
  RecommendationsByCycleChart,
  EvidenceByDepartmentChart,
} from "../../components/common/GapAssessmentCharts";
import { useAssessmentCycles } from "../../hooks/useAssessmentCycles";
import {
  Building2,
  Lock,
  Unlock,
  TrendingUp,
  ClipboardCheck,
  Plus,
  Target,
  CircleDashed,
  Users,
  AlertTriangle,
  FileCheck,
  Lightbulb,
  History,
  Layers,
  BarChart3,
  FileText,
  Eye,
  FileDown,
  FileSpreadsheet,
} from "lucide-react";
import {
  exportGapEvidenceExcel,
  exportGapEvidencePdf,
  fetchGapSubmission,
} from "../../utils/gapEvidenceExport";
import { showError } from "../../utils/sweetAlert";

const STATUS_BADGE = {
  completed: "success",
  submitted: "success",
  in_progress: "warning",
  not_started: "default",
};

const TABS = [
  {
    id: "overview",
    label: "Overview",
    icon: BarChart3,
    iconColor: "text-indigo-500",
    tooltip:
      "High-level counts: assessments, departments, compliance, and active cycle progress.",
  },
  {
    id: "departments",
    label: "Departments",
    icon: Building2,
    iconColor: "text-teal-500",
    tooltip:
      "Per-department status, compliance score, gaps, and recommendations for the selected cycle.",
  },
  {
    id: "users",
    label: "Users",
    icon: Users,
    iconColor: "text-blue-500",
    tooltip:
      "Who submitted assessments, completion by user, and recent activity (submissions, drafts, reopens).",
  },
  {
    id: "compliance",
    label: "Compliance",
    icon: Target,
    iconColor: "text-violet-500",
    tooltip:
      "Overall and department scores, year trends, and sections scoring below target.",
  },
  {
    id: "gaps",
    label: "Gaps",
    icon: AlertTriangle,
    iconColor: "text-red-500",
    tooltip:
      "Questions answered No or Partially — critical gaps are high-severity No answers.",
  },
  {
    id: "recommendations",
    label: "Recommendations",
    icon: Lightbulb,
    iconColor: "text-amber-500",
    tooltip:
      "Comments and notes users added on questions during the assessment (not auto-generated advice).",
  },
  {
    id: "evidence",
    label: "Evidence",
    icon: FileCheck,
    iconColor: "text-emerald-500",
    tooltip:
      "File attachments uploaded as supporting evidence on assessment questions.",
  },
  {
    id: "history",
    label: "History",
    icon: History,
    iconColor: "text-purple-500",
    tooltip:
      "Compare scores, gaps, and recommendations across past assessment cycles.",
  },
  {
    id: "cycles",
    label: "Cycles",
    icon: Layers,
    iconColor: "text-indigo-500",
    tooltip:
      "Manage assessment periods (usually yearly). Only one cycle is current at a time.",
  },
];

function SectionHeading({
  icon: Icon,
  iconColor = "text-indigo-600",
  title,
  tooltip,
  className = "mb-4",
}) {
  return (
    <h3
      className={`font-semibold text-slate-800 flex items-center gap-2 ${className}`}
    >
      {Icon && <Icon size={18} className={iconColor} />}
      {title}
      {tooltip && <HelpTooltip position="bottom" content={tooltip} />}
    </h3>
  );
}

function RecommendationCountIcon({ count }) {
  const n = Number(count) || 0;
  const label = `Noted in ${n} locked submission${n === 1 ? "" : "s"}`;
  return (
    <span
      className="inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-amber-50 text-amber-600"
      title={label}
      aria-label={label}
    >
      <Lightbulb size={14} />
    </span>
  );
}

function StatGrid({ items }) {
  return (
    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
      {items.map((s) => (
        <DashboardStatCard
          key={s.label}
          label={s.label}
          value={s.value}
          sub={s.sub}
          tooltip={s.tooltip}
          icon={s.icon}
          color={s.color || "blue"}
        />
      ))}
    </div>
  );
}

function listItems(block) {
  if (!block) return [];
  if (Array.isArray(block)) return block;
  return block.items || [];
}

function listPagination(block, pageSize = 20) {
  if (!block || Array.isArray(block) || block.items == null) return null;
  return {
    page: block.page || 1,
    pages: block.pages || 1,
    total: block.total || 0,
    pageSize: block.page_size || pageSize,
  };
}

function SectionTable({
  columns,
  rows,
  emptyTitle,
  emptyTooltip,
  pagination,
  onPageChange,
  itemLabel = "rows",
}) {
  if (!rows?.length) {
    return (
      <EmptyState title={emptyTitle || "No data"} description={emptyTooltip} />
    );
  }
  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="text-left text-slate-500 border-b">
            {columns.map((c) => (
              <th key={c.key} className="pb-2 pr-4 font-medium">
                {c.tooltip ? (
                  <LabelWithTooltip label={c.label} tooltip={c.tooltip} />
                ) : (
                  c.label
                )}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={row.id || i} className="border-b border-slate-50">
              {columns.map((c) => (
                <td key={c.key} className="py-2 pr-4 text-slate-700">
                  {c.render ? c.render(row) : (row[c.key] ?? "—")}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      {pagination && (
        <Pagination
          page={pagination.page}
          pages={pagination.pages}
          total={pagination.total}
          pageSize={pagination.pageSize}
          onPageChange={onPageChange}
          itemLabel={itemLabel}
          className="mt-3"
        />
      )}
    </div>
  );
}

export default function GapAssessmentAdmin() {
  const navigate = useNavigate();
  const { showNotification } = useApp();
  const [exportingKey, setExportingKey] = useState("");
  const {
    cycles,
    currentCycleId,
    refresh: refreshCycles,
  } = useAssessmentCycles();
  const [selectedCycleId, setSelectedCycleId] = useState("");
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [cycleModal, setCycleModal] = useState(false);
  const [activeTab, setActiveTab] = useState("overview");
  const [newCycle, setNewCycle] = useState({
    year: new Date().getFullYear() + 1,
    name: "",
    status: "open",
  });

  useEffect(() => {
    const onSetTab = (event) => {
      const tab = event.detail?.tab;
      if (tab && TABS.some((item) => item.id === tab)) {
        setActiveTab(tab);
      }
    };
    window.addEventListener("live-tour:set-tab", onSetTab);
    return () => window.removeEventListener("live-tour:set-tab", onSetTab);
  }, []);
  const PAGE_SIZE = 20;
  const [tablePages, setTablePages] = useState({
    departments: 1,
    userSubmissions: 1,
    userActivity: 1,
    userCompletion: 1,
    historicalRecords: 1,
    scoreChanges: 1,
    gapChanges: 1,
  });

  const setTablePage = (key, page) => {
    setTablePages((prev) => ({ ...prev, [key]: page }));
  };

  const cycleId = selectedCycleId || currentCycleId;

  const loadDashboard = useCallback(async () => {
    if (!cycleId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set("cycle_id", cycleId);
      params.set("page_size", String(PAGE_SIZE));
      params.set("departments_page", String(tablePages.departments));
      params.set("users_submissions_page", String(tablePages.userSubmissions));
      params.set("users_activity_page", String(tablePages.userActivity));
      params.set("users_completion_page", String(tablePages.userCompletion));
      params.set(
        "historical_records_page",
        String(tablePages.historicalRecords),
      );
      params.set("score_changes_page", String(tablePages.scoreChanges));
      params.set("gap_changes_page", String(tablePages.gapChanges));
      const res = await apiFetch(`/gap-assessment/admin/analytics?${params}`);
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      setAnalytics(await res.json());
    } catch (e) {
      showNotification(
        e.message || "Failed to load gap assessment dashboard",
        "error",
      );
    } finally {
      setLoading(false);
    }
  }, [cycleId, tablePages, showNotification]);

  useEffect(() => {
    if (currentCycleId && !selectedCycleId) setSelectedCycleId(currentCycleId);
  }, [currentCycleId, selectedCycleId]);

  useEffect(() => {
    setTablePages((p) => ({
      ...p,
      departments: 1,
      userSubmissions: 1,
      userActivity: 1,
      userCompletion: 1,
    }));
  }, [cycleId]);

  useEffect(() => {
    loadDashboard();
  }, [loadDashboard]);

  const handleCreateCycle = async () => {
    try {
      const res = await apiFetch("/gap-assessment/cycles", {
        method: "POST",
        body: JSON.stringify({
          year: Number(newCycle.year),
          name: newCycle.name || `${newCycle.year} Assessment`,
          status: newCycle.status,
          is_current: true,
        }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      showNotification("Assessment cycle created", "success");
      setCycleModal(false);
      await refreshCycles();
      const data = await res.json();
      if (data.cycle?.id) setSelectedCycleId(data.cycle.id);
    } catch (e) {
      showNotification(e.message, "error");
    }
  };

  // Open/Close (or reopen) an EXISTING cycle. This must PATCH the cycle by
  // id — never re-POST to /gap-assessment/cycles, since that endpoint
  // creates a new cycle and 409s with "Cycle for year X already exists"
  // when a cycle for that year is already on file. That mismatch was the
  // cause of the "already existing" error when toggling status.
  const handleToggleCycleStatus = async (cycle) => {
    const nextStatus = cycle.status === "open" ? "closed" : "open";
    const action = nextStatus === "open" ? "reopen" : "close";
    const ok = await confirmAction({
      title: `${action === "reopen" ? "Reopen" : "Close"} cycle?`,
      text: `"${cycle.name}" will be ${nextStatus === "open" ? "reopened for submissions" : "closed to new submissions"}.`,
      confirmText: action === "reopen" ? "Reopen" : "Close",
      icon: "question",
    });
    if (!ok) return;
    try {
      const res = await apiFetch(`/gap-assessment/cycles/${cycle.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status: nextStatus }),
      });
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      showNotification(
        `Cycle ${nextStatus === "open" ? "reopened" : "closed"}`,
        "success",
      );
      await refreshCycles();
      loadDashboard();
    } catch (e) {
      showNotification(e.message, "error");
    }
  };

  const exportSubmissionEvidence = async (submissionId, format) => {
    const key = `${submissionId}-${format}`;
    setExportingKey(key);
    try {
      const submission = await fetchGapSubmission(submissionId);
      if (format === "pdf") {
        await exportGapEvidencePdf(submission);
      } else {
        await exportGapEvidenceExcel(submission);
      }
    } catch (e) {
      showError(e.message || "Failed to export comments and evidence");
    } finally {
      setExportingKey("");
    }
  };

  const exportDepartmentEvidence = async (departmentId, departmentName, format) => {
    const key = `dept-${departmentId}-${format}`;
    setExportingKey(key);
    try {
      const params = new URLSearchParams();
      if (cycleId) params.set("cycle_id", cycleId);
      const res = await apiFetch(
        `/gap-assessment/admin/submissions${params.toString() ? `?${params}` : ""}`,
      );
      if (!res.ok) throw new Error("Failed to load department submissions");
      const data = await res.json();
      const rows = (data.submissions || []).filter(
        (row) => !departmentId || row.department_id === departmentId,
      );
      const combined = {
        org_name: rows[0]?.org_name,
        department_name: departmentName || rows[0]?.department_name,
        submitted_by_name: rows.map((row) => row.submitted_by_name).filter(Boolean).join(", "),
        created_at: rows[0]?.created_at,
        score: rows[0]?.score,
        result: {
          recommendations: rows.flatMap((row) => row.result?.recommendations || []),
        },
        answers: Object.assign({}, ...rows.map((row) => row.answers || {})),
        notes: Object.assign({}, ...rows.map((row) => row.notes || {})),
        owners: Object.assign({}, ...rows.map((row) => row.owners || {})),
      };
      if (format === "pdf") {
        await exportGapEvidencePdf(combined);
      } else {
        await exportGapEvidenceExcel(combined);
      }
    } catch (e) {
      showError(e.message || "Failed to export department evidence");
    } finally {
      setExportingKey("");
    }
  };

  const handleReopen = async (submissionId) => {
    const ok = await confirmAction({
      title: "Reopen assessment?",
      text: "This locked submission will be reopened for the user to edit.",
      confirmText: "Reopen",
      icon: "question",
    });
    if (!ok) return;
    try {
      const res = await apiFetch(
        `/gap-assessment/admin/submissions/${submissionId}/reopen`,
        { method: "POST" },
      );
      if (!res.ok) throw new Error((await res.json()).detail || "Failed");
      showNotification("Assessment reopened", "success");
      loadDashboard();
    } catch (e) {
      showNotification(e.message, "error");
    }
  };

  const fmtDate = (v) => (v ? new Date(v).toLocaleString() : "—");

  if (loading && !analytics) {
    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] animate-fade-in">
        <div className="relative mb-6">
          <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-lg">
            <ClipboardCheck size={36} className="text-white" />
          </div>
          <span className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full bg-white flex items-center justify-center shadow">
            <span className="block w-3 h-3 rounded-full bg-indigo-500 animate-pulse" />
          </span>
        </div>
        <h2 className="text-xl font-semibold text-slate-800 mb-1">
          Loading Dashboard
        </h2>
        <p className="text-sm text-slate-500 mb-6">
          Fetching gap assessment data for this cycle…
        </p>
        <div className="flex gap-1.5">
          {[0, 1, 2].map((i) => (
            <span
              key={i}
              className="block w-2 h-2 rounded-full bg-indigo-400 animate-bounce"
              style={{ animationDelay: `${i * 0.15}s` }}
            />
          ))}
        </div>
      </div>
    );
  }

  const hasNoSubmissions =
    !loading &&
    analytics &&
    (analytics.assessments?.total_assessments ?? 0) === 0;

  if (hasNoSubmissions) {
    return (
      <div className="space-y-6 animate-fade-in">
        {/* Header — keep visible so admin can still switch cycles / create new */}
        <div
          data-tour-id="tour-gap-admin-header"
          className="flex flex-wrap items-center justify-between gap-4"
        >
          <div className="flex items-start gap-3">
            <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md flex-shrink-0">
              <ClipboardCheck size={22} className="text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
                Gap Assessment Tracking
                <HelpTooltip
                  position="bottom"
                  content="Central dashboard to monitor DPDP gap assessments — completion, scores, gaps, user comments, and evidence across all departments."
                />
              </h1>
              <p className="text-sm text-slate-500 mt-1">
                {analytics?.cycle?.name || "Select cycle"} · No assessments
                submitted yet
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Select
              data-tour-id="tour-gap-admin-cycle"
              value={cycleId}
              onChange={(e) => setSelectedCycleId(e.target.value)}
              className="min-w-[180px]"
            >
              {cycles.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                  {c.is_current ? " (Current)" : ""}
                </option>
              ))}
            </Select>
            <Button
              data-tour-id="tour-gap-cycle"
              variant="secondary"
              onClick={() => setCycleModal(true)}
            >
              <Plus size={16} className="text-indigo-600" /> New Cycle
            </Button>
          </div>
        </div>

        {/* Empty state banner */}
        <div
          data-tour-id="tour-gap-admin-empty"
          className="rounded-2xl border-2 border-dashed border-indigo-200 bg-gradient-to-br from-indigo-50 via-white to-violet-50 px-8 py-14 flex flex-col items-center text-center shadow-sm"
        >
          <div className="relative mb-6">
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-indigo-100 to-violet-100 flex items-center justify-center shadow-inner">
              <ClipboardCheck size={38} className="text-indigo-400" />
            </div>
            <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full bg-amber-100 border-2 border-white flex items-center justify-center shadow">
              <AlertTriangle size={14} className="text-amber-500" />
            </span>
          </div>

          <h2 className="text-2xl font-bold text-slate-800 mb-2">
            No Assessments Submitted Yet
          </h2>
          <p className="text-slate-500 max-w-md mb-8 leading-relaxed">
            No department has submitted a gap assessment for the{" "}
            <span className="font-semibold text-indigo-600">
              {analytics?.cycle?.name || "current cycle"}
            </span>{" "}
            yet. Share the steps below with your department leads to get
            started.
          </p>

          {/* How-to steps */}
          <div className="grid sm:grid-cols-3 gap-4 w-full max-w-2xl mb-8">
            {[
              {
                step: "1",
                icon: Users,
                color: "text-blue-500",
                bg: "bg-blue-50",
                title: "Assign Department Leads",
                desc: "Make sure each department has a user with access to submit the gap assessment.",
              },
              {
                step: "2",
                icon: FileText,
                color: "text-indigo-500",
                bg: "bg-indigo-50",
                title: "Fill & Submit Assessment",
                desc: "Each lead opens the Gap Assessment form, answers all questions, and clicks Submit.",
              },
              {
                step: "3",
                icon: Lock,
                color: "text-violet-500",
                bg: "bg-violet-50",
                title: "Admin Reviews & Locks",
                desc: "Once submitted, you can review scores, gaps, and evidence here, then lock the record.",
              },
            ].map(({ step, icon: Icon, color, bg, title, desc }) => (
              <div
                key={step}
                className={`rounded-xl ${bg} border border-white p-5 text-left shadow-sm`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className="w-6 h-6 rounded-full bg-white flex items-center justify-center text-xs font-bold text-slate-700 shadow">
                    {step}
                  </span>
                  <Icon size={17} className={color} />
                </div>
                <p className="font-semibold text-slate-800 text-sm mb-1">
                  {title}
                </p>
                <p className="text-xs text-slate-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>

          {/* Cycle status pill */}
          <div className="flex items-center gap-2 px-4 py-2 rounded-full bg-white border border-slate-200 shadow-sm text-sm text-slate-600">
            <Layers size={14} className="text-indigo-400" />
            <span>Active cycle:</span>
            <span className="font-medium text-indigo-700">
              {analytics?.cycle?.name || "—"}
            </span>
            <Badge
              variant={
                analytics?.cycle?.status === "open" ? "success" : "default"
              }
            >
              {analytics?.cycle?.status || "—"}
            </Badge>
          </div>
        </div>

        <Modal
          open={cycleModal}
          onClose={() => setCycleModal(false)}
          title="Create Assessment Cycle"
        >
          <p className="text-sm text-slate-500 mb-4 flex items-start gap-1">
            <HelpTooltip content="A cycle defines the assessment period (usually one year). Creating a new cycle sets it as current so departments can submit fresh assessments." />
            <span>Configure a new assessment period below.</span>
          </p>
          <div className="space-y-4">
            <Input
              label="Year"
              type="number"
              value={newCycle.year}
              onChange={(e) =>
                setNewCycle({ ...newCycle, year: e.target.value })
              }
              hint="Calendar year for this assessment cycle."
            />
            <Input
              label="Name"
              placeholder="2027 Assessment"
              value={newCycle.name}
              onChange={(e) =>
                setNewCycle({ ...newCycle, name: e.target.value })
              }
              hint="Display name shown in the cycle dropdown."
            />
            <Select
              label="Status"
              value={newCycle.status}
              onChange={(e) =>
                setNewCycle({ ...newCycle, status: e.target.value })
              }
            >
              <option value="open">Open</option>
              <option value="closed">Closed</option>
            </Select>
            <Button onClick={handleCreateCycle}>Create & Set Current</Button>
          </div>
        </Modal>
      </div>
    );
  }

  const a = analytics || {};
  const departmentScores = a.department_scores || listItems(a.departments);
  const assessments = a.assessments || {};
  const departments = a.departments || {};
  const users = a.users || {};
  const compliance = a.compliance || {};
  const gaps = a.gaps || {};
  const recommendations = a.recommendations || {};
  const evidence = a.evidence || {};
  const historical = a.historical || {};
  const cycleTracking = a.cycles || {};

  return (
    <div className="space-y-6 animate-fade-in">
      <div
        data-tour-id="tour-gap-admin-header"
        className="flex flex-wrap items-center justify-between gap-4"
      >
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-indigo-500 to-violet-600 flex items-center justify-center shadow-md flex-shrink-0">
            <ClipboardCheck size={22} className="text-white" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2">
              Gap Assessment Tracking
              <HelpTooltip
                position="bottom"
                content="Central dashboard to monitor DPDP gap assessments — completion, scores, gaps, user comments, and evidence across all departments."
              />
            </h1>
            <p className="text-sm text-slate-500 mt-1">
              Assessments, compliance, gaps, recommendations & evidence —{" "}
              {a.cycle?.name || "Select cycle"}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Tooltip
            position="bottom"
            content="Choose which assessment cycle (year/period) to view. Data below reflects the selected cycle."
          >
            <div data-tour-id="tour-gap-admin-cycle">
              <Select
                value={cycleId}
                onChange={(e) => setSelectedCycleId(e.target.value)}
                className="min-w-[180px]"
              >
                {cycles.map((c) => (
                  <option key={c.id} value={c.id}>
                    {c.name}
                    {c.is_current ? " (Current)" : ""}
                  </option>
                ))}
              </Select>
            </div>
          </Tooltip>
          <Tooltip
            position="bottom"
            content="Start a new assessment cycle (e.g. next year). It becomes the current cycle for new submissions."
          >
            <Button
              data-tour-id="tour-gap-cycle"
              variant="secondary"
              onClick={() => setCycleModal(true)}
            >
              <Plus size={16} className="text-indigo-600" /> New Cycle
            </Button>
          </Tooltip>
        </div>
      </div>

      <div
        data-tour-id="tour-gap-admin-tabs"
        className="flex gap-2 overflow-x-auto overflow-y-visible pb-1"
      >
        {TABS.map(({ id, label, icon: Icon, iconColor, tooltip }) => (
          <Tooltip key={id} position="bottom" content={tooltip} maxWidth={320}>
            <button
              type="button"
              data-tour-id={`tour-gap-admin-tab-${id}`}
              onClick={() => setActiveTab(id)}
              className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition ${
                activeTab === id
                  ? "bg-indigo-600 text-white shadow-sm"
                  : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"
              }`}
            >
              <Icon
                size={15}
                className={activeTab === id ? "text-white" : iconColor}
              />
              {label}
            </button>
          </Tooltip>
        ))}
      </div>

      {activeTab === "overview" && (
        <div data-tour-id="tour-gap-admin-panel-overview" className="space-y-6">
          <div data-tour-id="tour-gap-admin-stats">
            <Card>
              <SectionHeading
                icon={ClipboardCheck}
                iconColor="text-indigo-600"
                title="Assessment Tracking"
                tooltip="Counts of all assessment records in this cycle — drafts, submitted, locked (final), and reopened."
              />
              <StatGrid
                items={[
                  {
                    label: "Total Assessments",
                    value: assessments.total_assessments ?? 0,
                    icon: FileText,
                    color: "blue",
                    tooltip:
                      "All assessment records including drafts and submissions.",
                  },
                  {
                    label: "Draft Assessments",
                    value: assessments.draft_assessments ?? 0,
                    icon: CircleDashed,
                    color: "amber",
                    tooltip: "Assessments started but not yet submitted.",
                  },
                  {
                    label: "Submitted",
                    value: assessments.submitted_assessments ?? 0,
                    icon: ClipboardCheck,
                    color: "indigo",
                    tooltip: "Assessments formally submitted by users.",
                  },
                  {
                    label: "Locked",
                    value: assessments.locked_assessments ?? 0,
                    icon: Lock,
                    color: "blue",
                    tooltip:
                      "Finalised submissions — read-only unless reopened by admin.",
                  },
                  {
                    label: "Reopened",
                    value: assessments.reopened_assessments ?? 0,
                    icon: Unlock,
                    color: "rose",
                    tooltip:
                      "Previously locked assessments unlocked for editing again.",
                  },
                  {
                    label: "Completion %",
                    value: `${assessments.completion_pct ?? 0}%`,
                    icon: Target,
                    color: "indigo",
                    tooltip:
                      "Percentage of departments with a completed (locked) assessment.",
                  },
                ]}
              />
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <ChartCard
              title="Department Status"
              tooltip="Share of departments completed, in progress, or not started."
            >
              <DepartmentStatusDonut departments={departments} />
            </ChartCard>
            <ChartCard
              title="Assessment Status"
              tooltip="Breakdown of locked, submitted, draft, and reopened assessments."
            >
              <AssessmentStatusDonut assessments={assessments} />
            </ChartCard>
          </div>

          <div className="grid md:grid-cols-3 gap-4">
            <ChartCard
              title="Compliance Score"
              tooltip="Average compliance score across all locked department submissions."
              className="md:col-span-1"
            >
              <ComplianceScoreGauge score={compliance.overall_score} />
            </ChartCard>
            <Card className="md:col-span-2">
              <SectionHeading
                icon={Building2}
                iconColor="text-teal-600"
                title="Department Scores"
                tooltip="Compliance percentage per department with a locked submission."
              />
              <DepartmentScoresBar rows={departmentScores} height={220} />
            </Card>
          </div>

          <Card>
            <SectionHeading
              icon={Layers}
              iconColor="text-indigo-600"
              title="Active Cycle Progress"
              tooltip="The current assessment period and how many departments have completed it."
            />
            <div className="grid sm:grid-cols-2 gap-6 items-center">
              <div>
                <p className="font-semibold text-slate-900 text-base">
                  {cycleTracking.active_cycle?.name || "—"}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  {cycleTracking.active_cycle?.year} ·{" "}
                  <span className="capitalize">{cycleTracking.active_cycle?.status}</span>
                </p>
                <p className="text-sm text-slate-700 mt-3 font-medium">
                  {cycleTracking.cycle_completion?.submitted_departments ?? 0}{" "}
                  of {cycleTracking.cycle_completion?.departments_total ?? 0}{" "}
                  departments completed
                </p>
              </div>
              <Tooltip
                position="top"
                maxWidth={280}
                content={`${cycleTracking.cycle_completion?.submitted_departments ?? 0} of ${cycleTracking.cycle_completion?.departments_total ?? 0} departments have a completed (locked) assessment — ${cycleTracking.cycle_completion?.completion_pct ?? 0}% of all departments.`}
              >
                <div className="space-y-2 cursor-help">
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className="text-2xl font-bold text-slate-900"
                      style={{ fontFamily: "Sora, sans-serif" }}
                    >
                      {cycleTracking.cycle_completion?.completion_pct ?? 0}%
                    </span>
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">
                      Complete
                    </span>
                  </div>
                  <ProgressBar
                    value={cycleTracking.cycle_completion?.completion_pct || 0}
                    size="md"
                    color="indigo"
                  />
                </div>
              </Tooltip>
            </div>
          </Card>
        </div>
      )}

      {activeTab === "departments" && (
        <div data-tour-id="tour-gap-admin-panel-departments" className="space-y-6">
          <Card>
            <SectionHeading
              icon={BarChart3}
              iconColor="text-teal-600"
              title="Department Compliance Overview"
              tooltip="Visual comparison of compliance scores across departments."
            />
            <DepartmentScoresBar rows={departmentScores} />
          </Card>
          <Card>
            <SectionHeading
              icon={Building2}
              iconColor="text-teal-600"
              title="Department-wise Status & Scores"
              tooltip="Each row is one department. Reopen unlocks a locked submission so the department can edit again."
            />
            <SectionTable
              emptyTitle="No departments"
              emptyTooltip="Add departments under Tenant → Departments to track assessments."
              columns={[
                {
                  key: "department_code",
                  label: "#",
                  tooltip: "Department serial number.",
                },
                {
                  key: "department_name",
                  label: "Department",
                  tooltip: "Department name.",
                },
                {
                  key: "status",
                  label: "Status",
                  tooltip:
                    "Not started, in progress (draft), or completed (locked submission).",
                  render: (r) => (
                    <Badge variant={STATUS_BADGE[r.status] || "default"}>
                      {r.status.replace("_", " ")}
                    </Badge>
                  ),
                },
                {
                  key: "score",
                  label: "Score",
                  tooltip:
                    "Compliance percentage from Yes / Partially / No answers.",
                  render: (r) => (r.score != null ? `${r.score}%` : "—"),
                },
                {
                  key: "compliance_label",
                  label: "Compliance",
                  tooltip:
                    "Rating label e.g. Strong, Partial, Needs attention.",
                },
                {
                  key: "gaps_total",
                  label: "Gaps",
                  tooltip: "Count of questions answered No or Partially.",
                },
                {
                  key: "recommendations_count",
                  label: "Recs",
                  tooltip:
                    "Comments, owners, or evidence attached to questions.",
                },
                {
                  key: "actions",
                  label: "",
                  render: (r) =>
                    r.is_locked && r.submission_id ? (
                      <Tooltip content="Unlock this assessment so the department can edit and resubmit.">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => handleReopen(r.submission_id)}
                          aria-label="Reopen"
                        >
                          <Unlock size={14} className="text-amber-600" />
                        </Button>
                      </Tooltip>
                    ) : null,
                },
              ]}
              rows={listItems(departments)}
              pagination={listPagination(departments, PAGE_SIZE)}
              onPageChange={(p) => setTablePage("departments", p)}
              itemLabel="departments"
            />
          </Card>
        </div>
      )}

      {activeTab === "users" && (
        <div data-tour-id="tour-gap-admin-panel-users" className="space-y-6">
          <Card>
            <SectionHeading
              icon={Users}
              iconColor="text-blue-600"
              title="Submissions by User"
              tooltip="Locked submissions showing who submitted, when, and the resulting score."
            />
            <SectionTable
              emptyTitle="No submissions yet"
              emptyTooltip="Submissions appear when departments complete and lock their assessments."
              columns={[
                {
                  key: "department_name",
                  label: "Department",
                  tooltip: "Department that submitted.",
                },
                {
                  key: "submitted_by_name",
                  label: "Submitted By",
                  tooltip: "User who submitted the final assessment.",
                },
                {
                  key: "submitted_at",
                  label: "Submitted At",
                  tooltip: "Date and time of final submission.",
                  render: (r) => fmtDate(r.submitted_at),
                },
                {
                  key: "last_updated_at",
                  label: "Last Updated",
                  tooltip: "Last save or edit before submission.",
                  render: (r) => fmtDate(r.last_updated_at),
                },
                {
                  key: "score",
                  label: "Score",
                  tooltip: "Final compliance score for this submission.",
                  render: (r) => `${r.score}%`,
                },
                {
                  key: "reopened_by_name",
                  label: "Reopened By",
                  tooltip:
                    "Admin who reopened a locked submission, if applicable.",
                  render: (r) => r.reopened_by_name || "—",
                },
                {
                  key: "actions",
                  label: "Evidence",
                  tooltip: "View the submission or download comments and evidence.",
                  render: (r) =>
                    r.submission_id ? (
                      <div className="flex items-center gap-1">
                        <Tooltip content="Open full submission in Form Responses">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() =>
                              navigate(
                                `/tenant/form-responses?tab=gap_assessment&id=${encodeURIComponent(r.submission_id)}`,
                              )
                            }
                            aria-label="View submission"
                          >
                            <Eye size={14} className="text-indigo-600" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Download comments and evidence as PDF">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={Boolean(exportingKey)}
                            onClick={() => exportSubmissionEvidence(r.submission_id, "pdf")}
                            aria-label="Export evidence PDF"
                          >
                            <FileDown size={14} className="text-indigo-600" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Download comments and evidence as Excel">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={Boolean(exportingKey)}
                            onClick={() => exportSubmissionEvidence(r.submission_id, "excel")}
                            aria-label="Export evidence Excel"
                          >
                            <FileSpreadsheet size={14} className="text-emerald-600" />
                          </Button>
                        </Tooltip>
                      </div>
                    ) : null,
                },
              ]}
              rows={listItems(users.submissions)}
              pagination={listPagination(users.submissions, PAGE_SIZE)}
              onPageChange={(p) => setTablePage("userSubmissions", p)}
              itemLabel="submissions"
            />
          </Card>
          <Card>
            <SectionHeading
              icon={Users}
              iconColor="text-blue-600"
              title="Assessment Completion by User"
              tooltip="How many departments each user has started or fully completed."
            />
            <SectionTable
              emptyTitle="No user activity"
              columns={[
                {
                  key: "user_name",
                  label: "User",
                  tooltip: "User display name.",
                },
                {
                  key: "departments_started",
                  label: "Depts Started",
                  tooltip:
                    "Departments where this user saved a draft or submission.",
                },
                {
                  key: "departments_completed",
                  label: "Depts Completed",
                  tooltip:
                    "Departments where this user submitted a locked assessment.",
                },
                {
                  key: "questions_answered",
                  label: "Questions Answered",
                  tooltip:
                    "Total questions answered across all their assessments.",
                },
              ]}
              rows={listItems(users.completion_by_user)}
              pagination={listPagination(users.completion_by_user, PAGE_SIZE)}
              onPageChange={(p) => setTablePage("userCompletion", p)}
              itemLabel="users"
            />
          </Card>
          <Card>
            <SectionHeading
              icon={History}
              iconColor="text-purple-600"
              title="User Activity History"
              tooltip="Recent actions: submissions, draft saves, and admin reopens."
            />
            <SectionTable
              emptyTitle="No activity"
              columns={[
                {
                  key: "at",
                  label: "When",
                  tooltip: "Date and time of the action.",
                  render: (r) => fmtDate(r.at),
                },
                {
                  key: "type",
                  label: "Type",
                  tooltip: "Action type: submission, draft_update, or reopen.",
                  render: (r) => (
                    <Tooltip
                      content={
                        r.type === "submission"
                          ? "Assessment was submitted and locked."
                          : r.type === "reopen"
                            ? "Admin reopened a locked assessment."
                            : r.type === "draft_update"
                              ? "User saved progress without submitting."
                              : r.type
                      }
                    >
                      <span>{r.type}</span>
                    </Tooltip>
                  ),
                },
                {
                  key: "user_name",
                  label: "User",
                  tooltip: "User who performed the action.",
                },
                {
                  key: "department_name",
                  label: "Department",
                  tooltip: "Affected department.",
                },
                {
                  key: "detail",
                  label: "Detail",
                  tooltip: "Additional context for this activity.",
                },
              ]}
              rows={listItems(users.activity)}
              pagination={listPagination(users.activity, PAGE_SIZE)}
              onPageChange={(p) => setTablePage("userActivity", p)}
              itemLabel="events"
            />
          </Card>
        </div>
      )}

      {activeTab === "compliance" && (
        <div data-tour-id="tour-gap-admin-panel-compliance" className="space-y-6">
          <StatGrid
            items={[
              {
                label: "Overall Score",
                value:
                  compliance.overall_score != null
                    ? `${compliance.overall_score}%`
                    : "—",
                icon: Target,
                color: "indigo",
                tooltip:
                  "Average compliance score across all locked department submissions.",
              },
              {
                label: "High-Risk Areas",
                value: (compliance.high_risk_areas || []).length,
                icon: AlertTriangle,
                color: "rose",
                tooltip:
                  "Assessment sections averaging below 40% — critical attention needed.",
              },
              {
                label: "Low-Scoring Sections",
                value: (compliance.low_scoring_sections || []).length,
                icon: TrendingUp,
                color: "amber",
                tooltip: "Sections averaging below 75% — room for improvement.",
              },
              {
                label: "Depts Scored",
                value: (compliance.by_department || []).length,
                icon: Building2,
                color: "blue",
                tooltip:
                  "Departments with at least one locked submission and a score.",
              },
            ]}
          />
          <Card>
            <SectionHeading
              icon={BarChart3}
              iconColor="text-violet-600"
              title="Department Compliance Chart"
              tooltip="Bar chart of compliance scores — green ≥75%, amber ≥50%, red below 50%."
            />
            <DepartmentScoresBar rows={compliance.by_department} />
          </Card>
          <Card>
            <SectionHeading
              icon={Target}
              iconColor="text-violet-600"
              title="Department Compliance Scores"
              tooltip="Compliance percentage per department from locked submissions only."
            />
            <SectionTable
              emptyTitle="No locked submissions"
              emptyTooltip="Scores appear after departments submit and lock their assessments."
              columns={[
                {
                  key: "department_code",
                  label: "#",
                  tooltip: "Department serial number.",
                },
                {
                  key: "department_name",
                  label: "Department",
                  tooltip: "Department name.",
                },
                {
                  key: "score",
                  label: "Score",
                  tooltip:
                    "Compliance percentage (Yes=100%, Partial=50%, No=0%).",
                  render: (r) => `${r.score}%`,
                },
                {
                  key: "compliance_label",
                  label: "Label",
                  tooltip: "Human-readable rating based on score.",
                },
              ]}
              rows={compliance.by_department || []}
            />
          </Card>
          <div className="grid lg:grid-cols-2 gap-6">
            <ChartCard
              title="Compliance Trend by Year"
              tooltip="Average score and completion rate for each assessment cycle."
            >
              <ComplianceTrendChart trendByYear={compliance.trend_by_year} />
            </ChartCard>
            <ChartCard
              title="Section Score Analysis"
              tooltip="Assessment sections with the lowest average scores — red is high risk, amber needs improvement."
            >
              <SectionScoresBar
                highRisk={compliance.high_risk_areas}
                lowScoring={compliance.low_scoring_sections}
              />
            </ChartCard>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <SectionHeading
                icon={TrendingUp}
                iconColor="text-indigo-600"
                title="Compliance Trend Details"
                tooltip="Average score and completion rate for each past assessment cycle."
              />
              {(compliance.trend_by_year || []).length === 0 ? (
                <EmptyState
                  title="No trend data"
                  description="Run assessments in multiple cycles to see trends."
                />
              ) : (
                <div className="space-y-4">
                  {(compliance.trend_by_year || []).map((t) => (
                    <Tooltip
                      key={t.cycle_id}
                      position="top"
                      content={`${t.name}: ${t.completion_pct ?? 0}% of departments completed${t.average_score != null ? ` · average score ${t.average_score}%` : ""}.`}
                    >
                      <div className="space-y-1.5 cursor-help">
                        <div className="flex justify-between text-sm">
                          <span className="font-semibold text-slate-800">{t.name}</span>
                          <span className="text-slate-600">
                            {t.average_score != null
                              ? `${t.average_score}% avg · ${t.completion_pct ?? 0}% done`
                              : `${t.completion_pct ?? 0}% complete`}
                          </span>
                        </div>
                        <ProgressBar value={t.completion_pct || 0} size="md" color="indigo" />
                      </div>
                    </Tooltip>
                  ))}
                </div>
              )}
            </Card>
            <Card>
              <SectionHeading
                icon={AlertTriangle}
                iconColor="text-red-600"
                title="High-Risk & Low-Scoring Sections"
                tooltip="Assessment topics with the lowest average scores across all departments."
              />
              <div className="space-y-3 max-h-80 overflow-y-auto">
                {(compliance.high_risk_areas || []).map((s) => (
                  <div
                    key={s.section_id}
                    className="p-2 rounded bg-red-50 text-sm"
                  >
                    <span className="font-medium">{s.title}</span>
                    <span className="text-red-600 ml-2">
                      {s.average_score}% avg
                    </span>
                  </div>
                ))}
                {(compliance.low_scoring_sections || [])
                  .filter(
                    (s) =>
                      !(compliance.high_risk_areas || []).some(
                        (h) => h.section_id === s.section_id,
                      ),
                  )
                  .map((s) => (
                    <div
                      key={s.section_id}
                      className="p-2 rounded bg-amber-50 text-sm"
                    >
                      <span className="font-medium">{s.title}</span>
                      <span className="text-amber-700 ml-2">
                        {s.average_score}% avg
                      </span>
                    </div>
                  ))}
                {(compliance.high_risk_areas || []).length === 0 &&
                  (compliance.low_scoring_sections || []).length === 0 && (
                    <EmptyState title="All sections scoring well" />
                  )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === "gaps" && (
        <div data-tour-id="tour-gap-admin-panel-gaps" className="space-y-6">
          <StatGrid
            items={[
              {
                label: "Total Gaps",
                value: gaps.total_gaps ?? 0,
                icon: AlertTriangle,
                color: "rose",
                tooltip:
                  "All questions answered No or Partially across locked submissions.",
              },
              {
                label: "Critical Gaps",
                value: gaps.critical_gaps ?? 0,
                icon: AlertTriangle,
                color: "rose",
                tooltip:
                  "High-severity questions answered No — highest priority for remediation.",
              },
              {
                label: "High Priority",
                value: gaps.high_priority_gaps ?? 0,
                icon: AlertTriangle,
                color: "amber",
                tooltip: "Medium-severity questions answered No or Partially.",
              },
            ]}
          />
          <Card>
            <SectionHeading
              icon={BarChart3}
              iconColor="text-red-600"
              title="Gaps by Department"
              tooltip="Stacked view of critical, high-priority, and other gaps per department."
            />
            <GapByDepartmentChart byDepartment={gaps.by_department} />
          </Card>
          <Card>
            <SectionHeading
              icon={AlertTriangle}
              iconColor="text-red-600"
              title="Department-wise Gaps"
              tooltip="Gap counts broken down by department for the selected cycle."
            />
            <SectionTable
              emptyTitle="No gap data"
              columns={[
                {
                  key: "department_code",
                  label: "#",
                  tooltip: "Department serial number.",
                },
                {
                  key: "department_name",
                  label: "Department",
                  tooltip: "Department name.",
                },
                {
                  key: "total_gaps",
                  label: "Total",
                  tooltip: "All No and Partially answers.",
                },
                {
                  key: "critical_gaps",
                  label: "Critical",
                  tooltip: "High-severity No answers.",
                },
                {
                  key: "high_priority_gaps",
                  label: "High Priority",
                  tooltip: "Medium-severity No or Partial answers.",
                },
              ]}
              rows={gaps.by_department || []}
            />
          </Card>
          <div className="grid lg:grid-cols-2 gap-6">
            <ChartCard
              title="Gap Trend by Year"
              tooltip="Total gaps across cycles — falling trend indicates improvement."
            >
              <GapTrendChart trendByYear={gaps.trend_by_year} />
            </ChartCard>
            <Card>
              <SectionHeading
                icon={TrendingUp}
                iconColor="text-amber-600"
                title="Gap Trend Details"
                tooltip="Total gaps reported in each assessment cycle — useful to see if compliance is improving."
              />
              <SectionTable
                emptyTitle="No historical gaps"
                columns={[
                  {
                    key: "year",
                    label: "Year",
                    tooltip: "Assessment cycle year.",
                  },
                  { key: "name", label: "Cycle", tooltip: "Cycle name." },
                  {
                    key: "total_gaps",
                    label: "Total Gaps",
                    tooltip: "Combined gaps across all submitted departments.",
                  },
                  {
                    key: "submitted_departments",
                    label: "Depts Submitted",
                    tooltip: "Departments that submitted in that cycle.",
                  },
                ]}
                rows={gaps.trend_by_year || []}
              />
            </Card>
          </div>
        </div>
      )}

      {activeTab === "recommendations" && (
        <div data-tour-id="tour-gap-admin-panel-recommendations" className="space-y-6">
          <StatGrid
            items={[
              {
                label: "Total Generated",
                value: recommendations.total ?? 0,
                icon: Lightbulb,
                color: "indigo",
                tooltip:
                  "Questions where users added a comment, owner, or evidence file.",
              },
              {
                label: "Open",
                value: recommendations.open_vs_addressed?.open ?? 0,
                sub: "Not tracked yet",
                icon: CircleDashed,
                color: "amber",
                tooltip:
                  "Recommendations not yet marked as addressed (tracking coming soon).",
              },
              {
                label: "Addressed",
                value: recommendations.open_vs_addressed?.addressed ?? 0,
                sub: recommendations.open_vs_addressed?.tracking_enabled
                  ? ""
                  : "Tracking not enabled",
                icon: FileCheck,
                color: "blue",
                tooltip:
                  "Recommendations marked as resolved (when tracking is enabled).",
              },
            ]}
          />
          <Card>
            <SectionHeading
              icon={Lightbulb}
              iconColor="text-amber-600"
              title="Most Common Recommendations"
              tooltip="Questions whose notes appear most often across locked department submissions. Hover the lightbulb to see how many submissions included that question."
            />
            {(recommendations.most_common || []).length === 0 ? (
              <EmptyState
                title="No recommendations"
                description="Comments appear when users add notes to questions during the assessment."
              />
            ) : (
              <ul className="space-y-2 text-sm">
                {(recommendations.most_common || []).map((r, i) => (
                  <li key={i} className="flex gap-2 p-2 rounded bg-slate-50">
                    <RecommendationCountIcon count={r.count} />
                    <span>{r.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <SectionHeading
                icon={Building2}
                iconColor="text-teal-600"
                title="By Department"
                tooltip="Number of comments/notes per department."
              />
              <RecommendationsByDepartmentChart
                byDepartment={recommendations.by_department}
              />
              <SectionTable
                emptyTitle="No recommendations"
                columns={[
                  {
                    key: "department_code",
                    label: "#",
                    tooltip: "Department serial number.",
                  },
                  {
                    key: "department_name",
                    label: "Department",
                    tooltip: "Department name.",
                  },
                  {
                    key: "count",
                    label: "Count",
                    tooltip: "Total comments and evidence entries.",
                  },
                ]}
                rows={recommendations.by_department || []}
              />
            </Card>
            <Card>
              <SectionHeading
                icon={Layers}
                iconColor="text-indigo-600"
                title="By Assessment Cycle"
                tooltip="Total recommendations recorded in each assessment year."
              />
              <RecommendationsByCycleChart byCycle={recommendations.by_cycle} />
              <SectionTable
                emptyTitle="No cycle data"
                columns={[
                  {
                    key: "year",
                    label: "Year",
                    tooltip: "Assessment cycle year.",
                  },
                  { key: "name", label: "Cycle", tooltip: "Cycle name." },
                  {
                    key: "total_recommendations",
                    label: "Total",
                    tooltip:
                      "Total comments and evidence entries in that cycle.",
                  },
                ]}
                rows={recommendations.by_cycle || []}
              />
            </Card>
          </div>
        </div>
      )}

      {activeTab === "evidence" && (
        <div data-tour-id="tour-gap-admin-panel-evidence" className="space-y-6">
          <StatGrid
            items={[
              {
                label: "Evidence Uploaded",
                value: evidence.uploaded ?? 0,
                icon: FileCheck,
                color: "blue",
                tooltip:
                  "Total file attachments uploaded on assessment questions.",
              },
              {
                label: "Missing Evidence",
                value: evidence.missing ?? 0,
                icon: AlertTriangle,
                color: "amber",
                tooltip:
                  "Answered questions where no supporting file was attached.",
              },
              {
                label: "Depts Complete",
                value: evidence.review_status?.departments_complete ?? 0,
                icon: Target,
                color: "indigo",
                tooltip: "Departments with all expected evidence uploaded.",
              },
              {
                label: "Depts Pending",
                value: evidence.review_status?.departments_pending ?? 0,
                icon: CircleDashed,
                color: "rose",
                tooltip: "Departments still missing some evidence files.",
              },
            ]}
          />
          <Card>
            <SectionHeading
              icon={FileCheck}
              iconColor="text-emerald-600"
              title="Evidence by Department"
              tooltip="File upload counts and review status per department."
            />
            <EvidenceByDepartmentChart byDepartment={evidence.by_department} />
            <SectionTable
              emptyTitle="No evidence data"
              columns={[
                {
                  key: "department_code",
                  label: "#",
                  tooltip: "Department serial number.",
                },
                {
                  key: "department_name",
                  label: "Department",
                  tooltip: "Department name.",
                },
                {
                  key: "uploaded",
                  label: "Uploaded",
                  tooltip: "Number of evidence files attached.",
                },
                {
                  key: "missing",
                  label: "Missing",
                  tooltip: "Questions answered but without an attachment.",
                },
                {
                  key: "review_status",
                  label: "Status",
                  tooltip:
                    "Complete = all evidence present; Partial = some missing; Pending = not reviewed.",
                  render: (r) => (
                    <Badge
                      variant={
                        r.review_status === "complete"
                          ? "success"
                          : r.review_status === "partial"
                            ? "warning"
                            : "default"
                      }
                    >
                      {r.review_status}
                    </Badge>
                  ),
                },
                {
                  key: "actions",
                  label: "Download",
                  tooltip: "Export comments and evidence files for this department.",
                  render: (r) =>
                    r.department_id ? (
                      <div className="flex items-center gap-1">
                        <Tooltip content="Download comments and evidence as PDF">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={Boolean(exportingKey)}
                            onClick={() =>
                              exportDepartmentEvidence(r.department_id, r.department_name, "pdf")
                            }
                            aria-label="Export department evidence PDF"
                          >
                            <FileDown size={14} className="text-indigo-600" />
                          </Button>
                        </Tooltip>
                        <Tooltip content="Download comments and evidence as Excel">
                          <Button
                            size="sm"
                            variant="ghost"
                            disabled={Boolean(exportingKey)}
                            onClick={() =>
                              exportDepartmentEvidence(r.department_id, r.department_name, "excel")
                            }
                            aria-label="Export department evidence Excel"
                          >
                            <FileSpreadsheet size={14} className="text-emerald-600" />
                          </Button>
                        </Tooltip>
                      </div>
                    ) : null,
                },
              ]}
              rows={evidence.by_department || []}
            />
          </Card>
        </div>
      )}

      {activeTab === "history" && (
        <div data-tour-id="tour-gap-admin-panel-history" className="space-y-6">
          <ChartCard
            title="Year-over-Year Comparison"
            tooltip="Average score, completion rate, and total gaps for each assessment cycle."
          >
            <YearComparisonChart yearComparison={historical.year_comparison} />
          </ChartCard>
          <Card>
            <SectionHeading
              icon={History}
              iconColor="text-purple-600"
              title="Year-over-Year Comparison"
              tooltip="Side-by-side summary of each assessment cycle: average score, completion, and total gaps."
            />
            <SectionTable
              emptyTitle="No historical cycles"
              emptyTooltip="Create and run assessments in multiple years to build history."
              columns={[
                {
                  key: "year",
                  label: "Year",
                  tooltip: "Assessment cycle year.",
                },
                { key: "name", label: "Cycle", tooltip: "Cycle name." },
                {
                  key: "average_score",
                  label: "Avg Score",
                  tooltip:
                    "Average compliance score across submitted departments.",
                  render: (r) =>
                    r.average_score != null ? `${r.average_score}%` : "—",
                },
                {
                  key: "completion_pct",
                  label: "Completion",
                  tooltip: "Percentage of departments that completed.",
                  render: (r) => `${r.completion_pct}%`,
                },
                {
                  key: "total_gaps",
                  label: "Total Gaps",
                  tooltip: "Combined gaps in that cycle.",
                },
                {
                  key: "submitted_departments",
                  label: "Depts",
                  tooltip: "Departments that submitted.",
                },
              ]}
              rows={historical.year_comparison || []}
            />
          </Card>
          <div className="grid lg:grid-cols-2 gap-6">
            <ChartCard
              title="Score Changes (YoY)"
              tooltip="Point change in compliance score from previous year — green is improvement."
            >
              <ScoreDeltaChart
                scoreChanges={
                  historical.score_changes_chart ||
                  listItems(historical.score_changes)
                }
              />
            </ChartCard>
            <ChartCard
              title="Gap Changes (YoY)"
              tooltip="Change in gap count from previous year — green means fewer gaps."
            >
              <GapDeltaChart
                gapChanges={
                  historical.gap_changes_chart ||
                  listItems(historical.gap_changes)
                }
              />
            </ChartCard>
          </div>
          <div className="grid lg:grid-cols-2 gap-6">
            <Card>
              <SectionHeading
                icon={TrendingUp}
                iconColor="text-emerald-600"
                title="Score Changes (YoY)"
                tooltip="Point change in compliance score from previous year to current year. Positive = improvement."
              />
              <SectionTable
                emptyTitle="Need 2+ cycles"
                emptyTooltip="At least two assessment cycles are required to compare year-over-year."
                columns={[
                  {
                    key: "department_name",
                    label: "Department",
                    tooltip: "Department name.",
                  },
                  {
                    key: "from_year",
                    label: "From",
                    tooltip: "Previous assessment year.",
                  },
                  {
                    key: "to_year",
                    label: "To",
                    tooltip: "Current assessment year.",
                  },
                  {
                    key: "score_delta",
                    label: "Δ Score",
                    tooltip: "Change in score points. + means improved.",
                    render: (r) =>
                      r.score_delta != null
                        ? `${r.score_delta > 0 ? "+" : ""}${r.score_delta}`
                        : "—",
                  },
                ]}
                rows={listItems(historical.score_changes)}
                pagination={listPagination(historical.score_changes, PAGE_SIZE)}
                onPageChange={(p) => setTablePage("scoreChanges", p)}
                itemLabel="departments"
              />
            </Card>
            <Card>
              <SectionHeading
                icon={AlertTriangle}
                iconColor="text-red-600"
                title="Gap Changes (YoY)"
                tooltip="Change in gap count from previous year. Negative = fewer gaps (good)."
              />
              <SectionTable
                emptyTitle="Need 2+ cycles"
                emptyTooltip="At least two assessment cycles are required to compare year-over-year."
                columns={[
                  {
                    key: "department_name",
                    label: "Department",
                    tooltip: "Department name.",
                  },
                  {
                    key: "from_year",
                    label: "From",
                    tooltip: "Previous assessment year.",
                  },
                  {
                    key: "to_year",
                    label: "To",
                    tooltip: "Current assessment year.",
                  },
                  {
                    key: "gap_delta",
                    label: "Δ Gaps",
                    tooltip: "Change in gap count. Negative means fewer gaps.",
                    render: (r) =>
                      r.gap_delta != null
                        ? `${r.gap_delta > 0 ? "+" : ""}${r.gap_delta}`
                        : "—",
                  },
                ]}
                rows={listItems(historical.gap_changes)}
                pagination={listPagination(historical.gap_changes, PAGE_SIZE)}
                onPageChange={(p) => setTablePage("gapChanges", p)}
                itemLabel="departments"
              />
            </Card>
          </div>
          <Card>
            <SectionHeading
              icon={FileText}
              iconColor="text-slate-600"
              title="Historical Assessment Records"
              tooltip="Every locked submission across all past cycles."
            />
            <SectionTable
              emptyTitle="No records"
              columns={[
                {
                  key: "cycle_year",
                  label: "Year",
                  tooltip: "Assessment cycle year.",
                },
                {
                  key: "department_name",
                  label: "Department",
                  tooltip: "Department name.",
                },
                {
                  key: "score",
                  label: "Score",
                  tooltip: "Final compliance score.",
                  render: (r) => `${r.score}%`,
                },
                {
                  key: "compliance_label",
                  label: "Compliance",
                  tooltip: "Rating label.",
                },
                {
                  key: "submitted_at",
                  label: "Submitted",
                  tooltip: "Submission date and time.",
                  render: (r) => fmtDate(r.submitted_at),
                },
                {
                  key: "is_locked",
                  label: "Locked",
                  tooltip: "Whether the submission is finalised.",
                  render: (r) => (r.is_locked ? "Yes" : "No"),
                },
              ]}
              rows={listItems(historical.assessment_records)}
              pagination={listPagination(
                historical.assessment_records,
                PAGE_SIZE,
              )}
              onPageChange={(p) => setTablePage("historicalRecords", p)}
              itemLabel="records"
            />
          </Card>
          <Card>
            <SectionHeading
              icon={Lightbulb}
              iconColor="text-amber-600"
              title="Historical Recommendations"
              tooltip="Most common comments and notes from past assessment cycles. Hover the lightbulb to see how many locked submissions included notes on that question."
            />
            {(historical.recommendations || []).length === 0 ? (
              <EmptyState title="No historical recommendations" />
            ) : (
              <ul className="space-y-2 text-sm">
                {(historical.recommendations || []).map((r, i) => (
                  <li key={i} className="flex gap-2 p-2 rounded bg-slate-50">
                    <RecommendationCountIcon count={r.count} />
                    <span>{r.text}</span>
                  </li>
                ))}
              </ul>
            )}
          </Card>
        </div>
      )}

      {activeTab === "cycles" && (
        <div data-tour-id="tour-gap-admin-panel-cycles" className="space-y-6">
          <div className="grid md:grid-cols-2 gap-4">
            <Card>
              <SectionHeading
                icon={Layers}
                iconColor="text-indigo-600"
                title="Active Cycle"
                tooltip="The cycle currently marked as active — new user submissions go against this period."
              />
              {cycleTracking.active_cycle ? (
                <>
                  <p className="font-medium text-lg">
                    {cycleTracking.active_cycle.name}
                  </p>
                  <p className="text-sm text-slate-600">
                    {cycleTracking.active_cycle.year} ·{" "}
                    <span className="capitalize">{cycleTracking.active_cycle.status}</span>
                  </p>
                </>
              ) : (
                <EmptyState
                  title="No active cycle"
                  description="Create a new assessment cycle to get started."
                />
              )}
            </Card>
            <Card>
              <SectionHeading
                icon={Target}
                iconColor="text-violet-600"
                title="Selected Cycle Completion"
                tooltip="Progress for the cycle selected in the dropdown above."
              />
              <p className="font-medium">
                {cycleTracking.cycle_completion?.name}
              </p>
              <Tooltip
                position="top"
                maxWidth={280}
                content={`${cycleTracking.cycle_completion?.submitted_departments ?? 0} of ${cycleTracking.cycle_completion?.departments_total ?? 0} departments completed — ${cycleTracking.cycle_completion?.completion_pct ?? 0}% progress for the selected cycle.`}
              >
                <div className="space-y-2 mt-2 cursor-help">
                  <div className="flex items-baseline justify-between gap-2">
                    <span
                      className="text-xl font-bold text-slate-900"
                      style={{ fontFamily: "Sora, sans-serif" }}
                    >
                      {cycleTracking.cycle_completion?.completion_pct ?? 0}%
                    </span>
                    <span className="text-xs text-slate-600">
                      {cycleTracking.cycle_completion?.submitted_departments}/
                      {cycleTracking.cycle_completion?.departments_total} departments
                    </span>
                  </div>
                  <ProgressBar
                    value={cycleTracking.cycle_completion?.completion_pct || 0}
                    size="md"
                    color="indigo"
                  />
                </div>
              </Tooltip>
            </Card>
          </div>
          <Card>
            <SectionHeading
              icon={Layers}
              iconColor="text-indigo-600"
              title="All Assessment Cycles"
              tooltip="Every assessment period configured for this tenant."
            />
            <SectionTable
              emptyTitle="No cycles"
              columns={[
                {
                  key: "year",
                  label: "Year",
                  tooltip: "Calendar year of the assessment.",
                },
                {
                  key: "name",
                  label: "Name",
                  tooltip: "Display name for the cycle.",
                },
                {
                  key: "status",
                  label: "Status",
                  tooltip:
                    "Open = accepting submissions; Closed = no new submissions.",
                  render: (r) => (
                    <Badge
                      variant={r.status === "open" ? "success" : "default"}
                    >
                      {r.status}
                    </Badge>
                  ),
                },
                {
                  key: "is_current",
                  label: "Current",
                  tooltip:
                    "Whether this is the active cycle for new submissions.",
                  render: (r) => (r.is_current ? "Yes" : "—"),
                },
                {
                  key: "actions",
                  label: "Actions",
                  tooltip:
                    "Open a closed cycle to resume submissions, or close an open cycle to stop them.",
                  render: (r) => (
                    <div className="flex gap-2">
                      <Button
                        variant={r.status === "open" ? "outline" : "primary"}
                        size="sm"
                        onClick={() => handleToggleCycleStatus(r)}
                      >
                        {r.status === "open" ? (
                          <span className="flex items-center gap-1">
                            <Lock size={14} /> Close
                          </span>
                        ) : (
                          <span className="flex items-center gap-1">
                            <Unlock size={14} /> Reopen
                          </span>
                        )}
                      </Button>
                      {!r.is_current && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            try {
                              const res = await apiFetch(
                                `/gap-assessment/cycles/${r.id}/set-current`,
                                { method: "POST" },
                              );
                              if (!res.ok)
                                throw new Error(
                                  (await res.json()).detail || "Failed",
                                );
                              showNotification(
                                "Cycle set as current",
                                "success",
                              );
                              await refreshCycles();
                              setSelectedCycleId(r.id);
                            } catch (e) {
                              showNotification(e.message, "error");
                            }
                          }}
                        >
                          Set current
                        </Button>
                      )}
                    </div>
                  ),
                },
              ]}
              rows={cycleTracking.all_cycles || []}
            />
          </Card>
          <Card>
            <SectionHeading
              icon={BarChart3}
              iconColor="text-indigo-600"
              title="Cycle-wise Reports"
              tooltip="Summary score and completion for each assessment period."
            />
            <SectionTable
              emptyTitle="No cycle reports"
              columns={[
                {
                  key: "year",
                  label: "Year",
                  tooltip: "Assessment cycle year.",
                },
                { key: "name", label: "Cycle", tooltip: "Cycle name." },
                {
                  key: "average_score",
                  label: "Avg Score",
                  tooltip: "Average compliance across submitted departments.",
                  render: (r) =>
                    r.average_score != null ? `${r.average_score}%` : "—",
                },
                {
                  key: "completion_pct",
                  label: "Completion",
                  tooltip: "Percentage of departments completed.",
                  render: (r) => `${r.completion_pct}%`,
                },
                {
                  key: "submitted_departments",
                  label: "Submitted",
                  tooltip: "Number of departments that submitted.",
                },
              ]}
              rows={compliance.trend_by_year || []}
            />
          </Card>
        </div>
      )}

      <Modal
        open={cycleModal}
        onClose={() => setCycleModal(false)}
        title="Create Assessment Cycle"
      >
        <p className="text-sm text-slate-500 mb-4 flex items-start gap-1">
          <HelpTooltip content="A cycle defines the assessment period (usually one year). Creating a new cycle sets it as current so departments can submit fresh assessments." />
          <span>Configure a new assessment period below.</span>
        </p>
        <div className="space-y-4">
          <Input
            label="Year"
            type="number"
            value={newCycle.year}
            onChange={(e) => setNewCycle({ ...newCycle, year: e.target.value })}
            hint="Calendar year for this assessment cycle."
          />
          <Input
            label="Name"
            placeholder="2027 Assessment"
            value={newCycle.name}
            onChange={(e) => setNewCycle({ ...newCycle, name: e.target.value })}
            hint="Display name shown in the cycle dropdown."
          />
          <Select
            label="Status"
            value={newCycle.status}
            onChange={(e) =>
              setNewCycle({ ...newCycle, status: e.target.value })
            }
          >
            <option value="open">Open</option>
            <option value="closed">Closed</option>
          </Select>
          <Button onClick={handleCreateCycle}>Create & Set Current</Button>
        </div>
      </Modal>
    </div>
  );
}
